/* r8 item 26: the product page's policy rows open right-side drawers (snippets/cq-pdp-policies.liquid). Each drawer is
   a native modal dialog (top layer, inert page, Escape), with the shared scroll lock (assets/cq-scroll-lock.js, r11 item 1)
   and the Configure popup's focus return and Tab wrap (assets/cq-configure.js). Without this script, or on a modified click, a row is a plain link to its page.
   Focus on open goes to the drawer's heading, as in the side cart; the Configure popup keeps focusing its close
   button (a deliberate split, noted in the round 8 handoff). */
(() => {
  if (customElements.get('cq-pdp-policies')) return;
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FOCUSABLE = 'button:not(:disabled),a[href],input:not([type="hidden"]):not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex="0"],iframe';

  customElements.define('cq-pdp-policies', class extends HTMLElement {
    connectedCallback() {
      if (this.cqEvents) return;
      this.cqEvents = new AbortController();
      const options = { signal: this.cqEvents.signal };
      this.rows = [];
      this.querySelectorAll('[data-cq-policy]').forEach((row) => {
        const dialog = document.getElementById(row.dataset.cqPolicy);
        if (!dialog || !this.contains(dialog)) return;
        this.rows.push(row);
        row.setAttribute('aria-haspopup', 'dialog');
        row.setAttribute('aria-controls', dialog.id);
        row.setAttribute('aria-expanded', 'false');
        row.addEventListener('click', (event) => {
          // A modified click (new tab or window) follows the row's link.
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.open(dialog, row);
        }, options);
        // Escape: the animated close. If Chrome skips a cancelable cancel (a quick second Escape during the close),
        // the dialog closes at once and the close event still cleans up.
        dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(dialog); }, options);
        dialog.addEventListener('close', () => this.cleanup(dialog, row), options);
        // A click on the scrim (outside the panel's rect) closes it, as in the Configure popup.
        dialog.addEventListener('click', (event) => {
          if (event.target.closest('[data-cq-drawer-close]')) { this.close(dialog); return; }
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close(dialog);
        }, options);
        // A native modal dialog lets Tab move out to the browser's own controls: wrap it inside the drawer.
        dialog.addEventListener('keydown', (event) => {
          if (event.key !== 'Tab') return;
          const controls = [...dialog.querySelectorAll(FOCUSABLE)].filter((element) => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement))) {
            event.preventDefault(); last?.focus();
          } else if (!event.shiftKey && (document.activeElement === last || !controls.includes(document.activeElement))) {
            event.preventDefault(); first?.focus();
          }
        }, options);
        // "Chat with us" opens the site chat (HubSpot conversations) once it has loaded, after the drawer closes; it
        // never sends a message. Otherwise it is a link to the contact page.
        dialog.querySelector('[data-cq-chat]')?.addEventListener('click', (event) => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const widget = window.HubSpotConversations?.widget;
          let loaded = false;
          try { loaded = Boolean(widget?.status?.()?.loaded) && typeof widget.open === 'function'; } catch (error) { loaded = false; }
          if (!loaded) return;
          event.preventDefault();
          if (dialog.open) dialog.close();
          try { widget.open(); } catch (error) { /* the contact page stays one tap away */ }
        }, options);
      });
      this.watchShipping(options);
    }

    // Release 1 lane A: the shipping row's state (free, threshold or checkout) for the variant and purchase type the
    // product page's form holds, rendered only when this product can reach more than one state. The map's keys are a
    // variant id (one-time) or "variant:plan" (Subscribe and Save on that plan), priced as the shopper will pay, so the row
    // matches the price the summary shows. All reachable states are already in the row (stacked in one grid cell), so a
    // change only swaps data-cq-ship-state: no layout shift. The form's id and selling_plan inputs are hidden inputs, whose
    // value and disabled state are attributes, so one observer sees every variant and purchase type change, whichever
    // script makes it (cq-option-picker.js update(), Bold, Loop); a picker for another product (quick view, Configure) has
    // its own form and is ignored.
    watchShipping(options) {
      const source = this.querySelector('[data-cq-ship-states]');
      const form = this.dataset.cqForm ? document.getElementById(this.dataset.cqForm) : null;
      if (!source || !form) return;
      let states = null;
      try { states = JSON.parse(source.textContent); } catch (error) { states = null; }
      if (!states) return;
      const order = ['checkout', 'threshold', 'free'];
      // Fail closed: a plan the map does not know takes the lowest state this variant can reach.
      const lowest = (id) => {
        const own = Object.keys(states).filter((key) => key === id || key.startsWith(`${id}:`)).map((key) => states[key]);
        return order.find((state) => own.includes(state));
      };
      const field = (name) => [...form.querySelectorAll(`input[name="${name}"]`)].find((input) => !input.disabled) || null;
      const apply = () => {
        const id = field('id')?.value || '';
        if (!(id in states)) return;
        const planInput = field('selling_plan');
        const plan = planInput && planInput.value ? planInput.value : '';
        const state = plan ? states[`${id}:${plan}`] || lowest(id) : states[id];
        if (state && this.dataset.cqShipState !== state) this.dataset.cqShipState = state;
      };
      this.shipObserver = new MutationObserver((records) => {
        if (records.some((record) => record.target.name === 'id' || record.target.name === 'selling_plan')) apply();
      });
      this.shipObserver.observe(form, { subtree: true, attributes: true, attributeFilter: ['value', 'disabled'] });
      // Backups for a script that sets a value without touching the attribute: the form's change and the picker's event.
      form.addEventListener('change', apply, options);
      document.addEventListener('cq:variant-change', apply, options);
      apply();
    }

    disconnectedCallback() {
      this.querySelectorAll('dialog[open]').forEach((dialog) => dialog.close());
      this.cqEvents?.abort();
      this.cqEvents = null;
      this.shipObserver?.disconnect();
      this.shipObserver = null;
    }

    open(dialog, row) {
      if (dialog.open) return;
      this.querySelectorAll('dialog[open]').forEach((other) => other.close());
      // r11 item 1: the shared scroll lock (assets/cq-scroll-lock.js). The page's overflow is never touched: the page
      // keeps its scrollbar (no empty strip), and its sticky header and photos stay where they are behind the scrim.
      window.cqScrollLock?.lock(dialog, { root: dialog, isOpen: () => dialog.open });
      clearTimeout(dialog.cqCloseTimer);
      dialog.classList.remove('is-closing');
      dialog.showModal();
      row.setAttribute('aria-expanded', 'true');
      dialog.querySelector('.cq-pdp-drawer-body')?.scrollTo(0, 0);
      dialog.querySelector('.cq-pdp-drawer-title')?.focus({ preventScroll: true });
    }

    // Slides out over 320 ms (.is-closing, the side cart's slide), then closes; with reduced motion it closes at once.
    close(dialog) {
      if (!dialog.open || dialog.classList.contains('is-closing')) return;
      if (reduced()) { dialog.close(); return; }
      dialog.classList.add('is-closing');
      const finish = () => {
        clearTimeout(dialog.cqCloseTimer);
        dialog.removeEventListener('transitionend', onEnd);
        if (dialog.open) dialog.close();
      };
      const onEnd = (event) => { if (event.target === dialog && event.propertyName === 'transform') finish(); };
      dialog.addEventListener('transitionend', onEnd);
      dialog.cqCloseTimer = setTimeout(finish, 340);
    }

    // Every close path ends here (the dialog's close event): unlock the scroll, reset the row, return focus.
    cleanup(dialog, row) {
      dialog.classList.remove('is-closing');
      window.cqScrollLock?.unlock(dialog);
      row.setAttribute('aria-expanded', 'false');
      if (row.isConnected) row.focus({ preventScroll: true });
    }
  });
})();
